import { ChevronDownIcon, ChevronUpIcon, EditIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Center,
  Collapse,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Spinner,
  Textarea as ChakraTextarea,
  Tooltip,
  useColorModeValue,
  useEditableControls,
  useToast,
} from '@chakra-ui/react';
import { conversationAction } from 'actions';
import { TEXT_CONSTANT } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { CONVERSATION_STATUS } from 'helpers/enum';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockMath, InlineMath } from 'react-katex';
import { useDispatch, useSelector } from 'react-redux';
import { User } from 'types';
import { Citation, Message } from 'types/reducers/conversation';

import TypingText from './TypingText';

const CITATIONS_EXPAND_HEIGHT_OFFSET = 100;

interface EditableTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const EditableTextarea = ({ value, onChange: onChangeProp, onKeyDown }: EditableTextareaProps) => {
  const { isEditing } = useEditableControls();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
      const minHeight = lineHeight + 10;
      textarea.style.height = `${Math.max(minHeight, Math.min(scrollHeight, 500))}px`;
    }
  }, []);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, adjustHeight]);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(adjustHeight, 0);
      return () => clearTimeout(timer);
    }
  }, [value, isEditing, adjustHeight]);

  if (!isEditing) {
    return null;
  }

  const lineCount = value ? value.split('\n').length : 1;
  const estimatedHeight = Math.max(40, lineCount * 20 + 10);

  return (
    <ChakraTextarea
      ref={textareaRef}
      sx={{
        height: 'auto',
        minHeight: `${estimatedHeight}px`,
        lineHeight: '1.4',
      }}
      pos='relative'
      zIndex={1}
      overflow='hidden'
      w='100%'
      minH={`${estimatedHeight}px`}
      px={0}
      bg='inherit'
      _focus={{ outline: 'none', boxShadow: 'none' }}
      whiteSpace='pre-line'
      resize='none'
      onChange={(e) => {
        onChangeProp(e.target.value);
        setTimeout(adjustHeight, 0);
      }}
      onKeyDown={onKeyDown}
      value={value || ''}
    />
  );
};

interface MessageChatProps {
  data: Message;
  onOpenFile: (_citation: Citation) => void;
  status: CONVERSATION_STATUS;
  isLast: boolean;
  user: User;
  cancelEditRef: React.RefObject<HTMLButtonElement>;
}

const MessageChat = ({
  data,
  onOpenFile,
  status,
  isLast,
  user,
  cancelEditRef,
}: MessageChatProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const citationColor = useColorModeValue('rgb(4, 82, 200)', '#a7c9f8');
  const citationsRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { current, processingStreamingChunkStatus } = useSelector((store) => store.conversations);

  const [isHovering, setIsHovering] = useState(false);
  const [isShowFullCitation, setIsShowFullCitation] = useState(false);
  const [isShowViewMoreButton, setIsShowViewMoreButton] = useState(false);
  const [question, setQuestion] = useState('');

  const isError = data?.status === 'error';
  const isShowEditIcon = isHovering && isLast && status !== CONVERSATION_STATUS.GENERATE_ANSWER;
  const hasCitation = data?.citations && data.citations?.length > 0;

  const cleanLatex = (latexString: string): string => {
    return latexString
      .replace(/\\\[\s*\n/g, '\\[')
      .replace(/\n\s*\\\]/g, ' \\]')
      .trim();
  };

  const formatAnswer = (answer: string) => {
    const lines = answer.split('\n');
    let isTable = false;
    let isCodeBlock = false;
    let tableLines: string[] = [];
    let codeBlockLines: string[] = [];
    const bulletListRegex = /^\s*([*+-])\s+/;
    const blockQuoteRegex = /^\s*>/;

    const renderedLines = lines.map((line, lineIndex) => {
      switch (true) {
        case line.trim().startsWith('|') && line.includes('|'):
          isTable = true;
          tableLines.push(line);

          if (lineIndex === lines.length - 1 || !lines[lineIndex + 1].includes('|')) {
            isTable = false;
            const tableContent = tableLines.join('\n');
            tableLines = [];
            return (
              <div key={lineIndex} className='table-wrapper'>
                <Markdown className='table-content' options={{ enforceAtxHeadings: true }}>
                  {tableContent}
                </Markdown>
              </div>
            );
          }
          return null;

        case line.trim().startsWith('```'):
          if (!isCodeBlock) {
            isCodeBlock = true;
          } else {
            isCodeBlock = false;
            const codeBlockContent = codeBlockLines.join('\n');
            codeBlockLines = [];

            const innerRenderedContent = formatAnswer(codeBlockContent);
            return (
              <div key={lineIndex} className='codeblock'>
                <pre>
                  <code>{innerRenderedContent}</code>
                </pre>
              </div>
            );
          }
          return null;

        case isTable:
          tableLines.push(line);
          return null;

        case isCodeBlock:
          codeBlockLines.push(line);
          return null;

        case blockQuoteRegex.test(line.trim()):
          const blockQuoteContent = line.replace(blockQuoteRegex, '').trim();
          return (
            <blockquote key={lineIndex} className='custom-blockquote'>
              {blockQuoteContent}
            </blockquote>
          );

        default:
          if (bulletListRegex.test(line.trim())) {
            const indentation = line.match(/^\s*/)?.[0]?.length || 0;
            const indentationLevel = Math.floor(indentation / 2);

            const bulletMatch = line.trim().match(bulletListRegex);

            if (bulletMatch) {
              const bulletSymbol = bulletMatch[0];

              return (
                <div
                  key={`bullet-${lineIndex}`}
                  style={{ paddingLeft: `${indentationLevel * 20}px` }}
                >
                  <span>{bulletSymbol}</span>
                  {renderMixedContent(line.trim().slice(bulletMatch[0].length))}
                </div>
              );
            }
          }

          return <div key={lineIndex}>{renderMixedContent(line)}</div>;
      }
    });

    return renderedLines;
  };

  const renderMixedContent = (line: string) => {
    const inlineMathPattern = /\\\((.*?)\\\)/g;
    const blockMathPattern = /\\\[(.*?)\\\]/;
    const markdownOptions = {
      enforceAtxHeadings: true, // Only parse headers when # is followed by space
    };

    const elements: JSX.Element[] = [];
    let remaining = line;
    let blockMatch;

    if ((blockMatch = blockMathPattern.exec(remaining)) !== null) {
      const [fullBlockMatch, blockFormula] = blockMatch;

      const precedingText = remaining.slice(0, blockMatch.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <div key={elements.length} style={{ display: 'block', margin: '0' }}>
          <BlockMath>{blockFormula}</BlockMath>
        </div>
      );

      remaining = remaining.slice(blockMatch.index + fullBlockMatch.length);
    }

    let lastIndex = 0;
    let match;
    while ((match = inlineMathPattern.exec(remaining)) !== null) {
      const [fullMatch, formula] = match;

      const precedingText = remaining.slice(lastIndex, match.index);
      if (precedingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{precedingText.trim()}</Markdown>
          </span>
        );
      }

      elements.push(
        <span
          key={elements.length}
          style={{ display: 'inline', marginLeft: '10px', marginRight: '5px' }}
        >
          <InlineMath>{formula.trim()}</InlineMath>
        </span>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < remaining.length) {
      const remainingText = remaining.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key={elements.length}>
            <Markdown options={markdownOptions}>{remainingText.trim()}</Markdown>
          </span>
        );
      }
    }

    return <>{elements}</>;
  };

  const cleanAnswer = cleanLatex(data.answer ?? '');

  const lastAnswerResult = data.answer ? (
    <div className='markdown-container'>{formatAnswer(cleanAnswer)}</div>
  ) : (
    <div>{TEXT_CONSTANT.CONVERSATION.ERROR_ANSWER}</div>
  );

  const onMouseEnter = () => {
    setIsHovering(true);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
  };

  const onToggleShowFullCitation = () => setIsShowFullCitation(!isShowFullCitation);

  const handleKeyDownEditable = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !question) {
      e.preventDefault();
      return false;
    }
  };

  const onSubmidEditQuestion = (question: string) => {
    if (question) {
      dispatch(conversationAction.editQuestion({ question, created_at: new Date().toISOString() }));
      dispatch(conversationAction.updateProcessingStreamingChunkStatus(true));
      dispatch(conversationAction.setTimeOutForFinalAnswer(true));
    } else {
      toast({
        title: `Question must not empty`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const citationsHeight = citationsRef?.current?.clientHeight ?? 0;
    const isCitationsCollapsable = citationsHeight > CITATIONS_EXPAND_HEIGHT_OFFSET;

    setIsShowViewMoreButton(isCitationsCollapsable);
    setIsShowFullCitation(!isCitationsCollapsable);
  }, [data?.citations]);

  useEffect(() => {
    setQuestion(data?.question);
  }, [data?.question]);

  useEffect(() => {
    if (data.answer) {
      dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
      dispatch(conversationAction.setTimeOutForFinalAnswer(false));
      dispatch(conversationAction.clearStreamingAnswerChunks());
    }
  }, [data.answer]);

  return (
    <>
      <Flex justify='flex-end' pt={{ base: 5 }}>
        <Box
          pos='relative'
          w={{ base: '55%', md: '55%' }}
          fontSize={{ base: 'smaller', sm: 'small', md: 'medium' }}
        >
          <Editable
            key={data?.question}
            isPreviewFocusable={false}
            onCancel={() => {
              setQuestion(data?.question || '');
            }}
            onChange={(value) => setQuestion(value)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onSubmit={onSubmidEditQuestion}
            selectAllOnFocus={false}
            submitOnBlur={false}
            value={question}
          >
            <Box
              pos='relative'
              mb={2}
              px={5}
              py={4}
              pe={10}
              color='#ffffff'
              bg='main_color'
              opacity={isError ? 0.5 : 1}
              borderRadius='10px 10px 0px 10px'
              shadow='shadow_box'
              whiteSpace='pre-line'
            >
              {isLast ? (
                <>
                  <EditablePreview maxW='100%' whiteSpace='pre-line' />
                  <Box
                    as={EditableInput}
                    sx={{
                      display: 'none !important',
                      visibility: 'hidden !important',
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                  />
                  <EditableTextarea
                    value={question}
                    onChange={setQuestion}
                    onKeyDown={handleKeyDownEditable}
                  />
                </>
              ) : (
                <>{data?.question}</>
              )}
            </Box>
            <Box fontSize={{ base: 'small' }} textAlign='right'>
              <EditableControls
                isShowEditIcon={isShowEditIcon}
                cancelRef={cancelEditRef}
                value={question}
              />
              <Box as='span' color='red'>
                {isError && 'Error sent'}
              </Box>
              {timeFormat(data?.created_at)}
            </Box>
          </Editable>
        </Box>
        <Flex justify='flex-end' direction='column' ms='12px'>
          <Avatar
            color='grey.50'
            bg='main_color'
            name={user.name?.replace(/\s+/g, ' ')}
            size={{ base: 'sm' }}
          />
        </Flex>
      </Flex>
      <Flex
        justify='flex-start'
        overflowX='hidden'
        w={{ base: '100%', md: '100%' }}
        mt={{ base: 3 }}
      >
        {!isError ? (
          <>
            <Flex justify='flex-end' direction='column' me={3}>
              <Avatar color='#000000' bg='#ffffff' name='Answer' size='sm' />
            </Flex>
            {
              <>
                {processingStreamingChunkStatus && !data.answer && !current.answerChunks.chunk ? (
                  <>
                    <Flex
                      mb={2}
                      p={{ base: 2, lg: 5 }}
                      fontSize={{ base: 'small', sm: 'small', md: 'medium' }}
                      bg='bg_box'
                      borderRadius='10px 10px 10px 0px'
                      shadow='shadow_box'
                      whiteSpace='pre-line'
                    >
                      <Spinner me={4} /> {t('common.loading_answer')}
                    </Flex>
                  </>
                ) : (
                  <>
                    <>
                      <Box w={{ base: '80%', md: '80%' }}>
                        <Box
                          className='mess_container'
                          mb={2}
                          px={7}
                          py={4}
                          fontSize={{ base: 'smaller', sm: 'small', md: 'medium' }}
                          textAlign='justify'
                          bg='bg_box'
                          borderRadius='10px 10px 10px 0px'
                          shadow='shadow_box'
                          whiteSpace='pre-line'
                        >
                          {!data.answer && current.answerChunks.chunk ? (
                            <>
                              <TypingText />
                            </>
                          ) : (
                            lastAnswerResult
                          )}
                          {hasCitation && (
                            <Box mt='5' textAlign='left'>
                              <Collapse
                                in={isShowFullCitation}
                                startingHeight={CITATIONS_EXPAND_HEIGHT_OFFSET}
                              >
                                <Flex ref={citationsRef}>
                                  <Box me={2} fontWeight={'bold'}>
                                    {t('chat.citations')}
                                  </Box>
                                  <Box overflow='hidden' textOverflow='ellipsis'>
                                    {data?.citations?.map((citation, index) => (
                                      <Box
                                        key={index}
                                        mb={1}
                                        color={citationColor}
                                        fontSize={{
                                          base: 'small',
                                          md: 'medium',
                                        }}
                                        _hover={{ textDecoration: 'underline' }}
                                        cursor='pointer'
                                        onClick={() => onOpenFile(citation)}
                                      >
                                        {citation.file_name}
                                      </Box>
                                    ))}
                                  </Box>
                                </Flex>
                              </Collapse>
                            </Box>
                          )}
                          {isShowViewMoreButton && (
                            <Center>
                              <Button
                                mt={3}
                                color='color_main_text'
                                onClick={onToggleShowFullCitation}
                                rightIcon={
                                  isShowFullCitation ? (
                                    <ChevronUpIcon boxSize='1.5rem' />
                                  ) : (
                                    <ChevronDownIcon boxSize='1.5rem' />
                                  )
                                }
                                size='sm'
                                variant='link'
                              >
                                {isShowFullCitation ? t('common.showLess') : t('common.showMore')}
                              </Button>
                            </Center>
                          )}
                        </Box>
                        {data.answer && (
                          <Box fontSize={12} textAlign='left'>
                            {timeFormat(data?.updated_at)}
                          </Box>
                        )}
                      </Box>
                    </>
                  </>
                )}
              </>
            }
          </>
        ) : (
          <Flex
            mb={2}
            p={5}
            fontSize={{ base: 'small', sm: 'small', md: 'medium' }}
            bg='bg_box'
            borderRadius='10px 10px 10px 0px'
            shadow='shadow_box'
            whiteSpace='pre-line'
          >
            <>{TEXT_CONSTANT.CONVERSATION.NOT_FOUND_ANSWER}</>
          </Flex>
        )}
      </Flex>
    </>
  );
};

interface EditableControlsProps {
  isShowEditIcon: boolean;
  value: string;
  cancelRef: React.RefObject<HTMLButtonElement>;
}

function EditableControls({ isShowEditIcon, cancelRef, value }: EditableControlsProps) {
  const { t } = useTranslation();
  const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
    useEditableControls();

  return isEditing ? (
    <ButtonGroup
      justifyContent='end'
      me='10'
      fontSize={{ base: 'small', sm: 'small', md: 'medium' }}
      size='sm'
    >
      <Button {...getCancelButtonProps()} ref={cancelRef} borderRadius={6} variant='ghost'>
        {t('common.cancel')}
      </Button>
      <Button {...getSubmitButtonProps()} borderRadius={6} isDisabled={!value} variant='action'>
        {t('common.update')}
      </Button>
    </ButtonGroup>
  ) : (
    <>
      {isShowEditIcon && (
        <Box justifyContent='center'>
          <Tooltip label={t('common.editText')}>
            <Box
              pos='absolute'
              top={2}
              right={3}
              _hover={{ cursor: 'pointer' }}
              {...getEditButtonProps()}
            >
              <EditIcon boxSize={'1.0rem'} color='white' />
            </Box>
          </Tooltip>
        </Box>
      )}
    </>
  );
}

export default MessageChat;
